import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { difference, isEmpty, isEqual, sortBy, union, uniqBy } from 'lodash/fp';

import { BodyText3, FlexRow, SvgIcon, Toggle } from '@portal/common/components';
import {
    AGE_GROUP_ID_KEY,
    CAUSE_ID_KEY,
    COMORBID_CAUSE_ID_KEY,
    DAY_KEY,
    FORECAST_SCENARIO_ID_KEY,
    LOCATION_ID_KEY,
    MEASURE_ID_KEY,
    MONTH_COUNT_KEY,
    RACE_ID_KEY,
    RESPONSE_ID_KEY,
    ROUND_ID_KEY,
    VACCINATION_STATUS_ID_KEY,
    YEAR_KEY,
} from '@portal/common/models/data-key';
import {
    isLocationTypeCountryOrSubnational,
    isLocationTypeGlobal,
    isLocationTypeSubnational,
} from '@portal/common/models/location-types';
import { styled } from '@portal/common/theme';
import { COMPACT_BUTTON_SIZE } from '@portal/common/theme/theme-config';
import { DataGranularityKey, DropdownOption, RefinementFilterConfig } from '@portal/common/types';
import {
    firstAndLastFromArray,
    getLocalePrefixForGranularityKey,
} from '@portal/common/utility/filters-helpers';

import AgeGroupsDropdown from '../../components/AgeGroupsDropdown';
import ChartDropdown from '../../components/ChartDropdown';
import ChartHierarchicalDropdown from '../../components/ChartHierarchicalDropdown';
import ChartRangeSlider from '../../components/ChartRangeSlider';
import RaceDropdown from '../../components/RaceDropdown';
import VaccinationStatusDropdown from '../../components/VaccinationStatusDropdown';
import _ from '../../locale';
import { getRoundLabelOverride } from '../../models/data-column-label';
import { HEALTH_THREATS_METRICS_PATH } from '../../router/paths';
import {
    changeSelectedRefinementFilters,
    removeVisibleRefinementFilters,
    validateIncludeNowcastsComplexityLimit,
} from '../../store/data-explorer/actions';
import {
    getDataToolConfig,
    getEntityHierarchies,
    getHasNowcastYearsBeyondPublished,
    getIncludeNowcasts,
    getRefinedLocationChildrenById,
    getSelectedConditions,
    getSelectedConditionsDataTypesDefaultFilters,
    getSelectedConditionsDataTypesGranularity,
    getSelectedConditionsRefinedGranularity,
    getSelectedDataProject,
    getSelectedDatasetForecastDataCalculationMap,
    getSelectedRefinementFiltersWithFallbackToDefaultFilters,
} from '../../store/data-explorer/selectors';
import { getLocationsById } from '../../store/entity-hierarchies/selectors';
import { getOrganization } from '../../store/organization/selectors';
import { getSelectedDataTool } from '../../store/user-settings/selectors';
import SmartFilterOptionSelector from '../SmartFilterOptionSelector';
import { useDataExplorerLayout } from './context';

const SELECT_ALL_DISABLED_KEYS = [CAUSE_ID_KEY, ROUND_ID_KEY, DAY_KEY];

const SubnationalButton = styled(FlexRow)<{ disabled: boolean }>(({ disabled }) => ({
    ...(disabled && {
        cursor: 'default',
        pointerEvents: 'none',
    }),
}));

const SubnationalSvgIcon = styled(SvgIcon)<{
    disabled: boolean;
    isActive: boolean;
}>(({ theme, disabled, isActive }) => ({
    color: theme.color.disabled,
    ':hover, :focus': {
        color: theme.color.green1,
    },
    ...(isActive && {
        color: theme.color.green1,
    }),
    ...(disabled && {
        color: theme.color.disabled,
        cursor: 'default',
        pointerEvents: 'none',
    }),
}));

const ListItem = styled.div<{ disabled: boolean }>(({ theme, disabled }) => ({
    ...(disabled && {
        color: theme.color.disabled,
    }),
}));

const RemoveButton = styled.button(({ theme }) => ({
    border: 'none',
    background: 'none',
    fontSize: '1.6rem',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'inline-block',
    color: theme.color.primary1,
    ':hover, :focus': {
        color: theme.color.primary2,
    },
    ':active': {
        color: theme.color.primary3,
    },
}));

const NowcastsToggleContainer = styled(FlexRow)({
    marginTop: -10,
    marginBottom: 10,
});

type Props = {
    refinementFiltersConfig: RefinementFilterConfig;
    granularityKey: DataGranularityKey;
    selectedValue?: number[];
    availableRefinementFilters: {};
    hideDelete?: boolean;
    hideSelectAll?: boolean;
    mode?: 'slider' | 'dropdown';
    onChange?: (val: string) => void;
    options?: DropdownOption[];
    label?: string;
    onRemove?: (granularityKey: DataGranularityKey) => void;
    preprocessOptions?: (options: DropdownOption[]) => any;
    onlySelectAllKeys?: DataGranularityKey[];
};

const RefinementFilter: React.FC<Props> = ({
    availableRefinementFilters,
    granularityKey,
    hideDelete,
    hideSelectAll,
    onChange,
    selectedValue,
    refinementFiltersConfig,
    mode,
    label: customLabel,
    preprocessOptions,
    onRemove,
    onlySelectAllKeys,
}) => {
    const dispatch = useDispatch();
    const { isCompact } = useDataExplorerLayout();

    const entityHierarchies = useSelector(getEntityHierarchies);
    const locationsById = useSelector(getLocationsById);
    const refinedLocationChildrenById = useSelector(getRefinedLocationChildrenById);
    const granularity = useSelector(getSelectedConditionsDataTypesGranularity);
    const refinedGranularity = useSelector(getSelectedConditionsRefinedGranularity);
    const selectedRefinementFilters = useSelector(
        getSelectedRefinementFiltersWithFallbackToDefaultFilters
    );
    const selectedConditions = useSelector(getSelectedConditions);
    const defaultFilters = useSelector(getSelectedConditionsDataTypesDefaultFilters);
    const { areRefinementFiltersSmartOptionsDisabled } = useSelector(getDataToolConfig);
    const dataTool = useSelector(getSelectedDataTool);
    const organization = useSelector(getOrganization);
    const forecastDataCalculationMap = useSelector(getSelectedDatasetForecastDataCalculationMap);
    const includeNowcasts = useSelector(getIncludeNowcasts);
    const hasNowcastYearsBeyondPublished = useSelector(getHasNowcastYearsBeyondPublished);
    const selectedDataProject = useSelector(getSelectedDataProject);

    const handleToggleIncludeNowcasts = useCallback(() => {
        dispatch(validateIncludeNowcastsComplexityLimit());
    }, [dispatch]);

    const handleRemoveRefinementClick = (granularityKey: DataGranularityKey) => {
        if (onRemove) {
            onRemove(granularityKey);
            return;
        }

        dispatch(removeVisibleRefinementFilters(granularityKey));

        // when removing filter the value should be set to default if it is different
        const defaultValue = defaultFilters[granularityKey];
        if (!isEqual(defaultValue, selectedRefinementFilters[granularityKey])) {
            dispatch(
                changeSelectedRefinementFilters({
                    [granularityKey]: defaultValue,
                })
            );
        }
    };

    if (availableRefinementFilters == null) {
        return null;
    }

    const fallbackGranularity = refinedGranularity == null ? granularity : refinedGranularity;

    const label =
        customLabel ||
        getRoundLabelOverride(granularityKey, selectedDataProject) ||
        _('refinement_' + granularityKey);

    const handleRenderOption = (
        o: DropdownOption,
        { optionValueGetter, optionLabelGetter, optionDisabledGetter, optionSearchTermGetter },
        { value, handleChange }
    ) => {
        // find location entity by option Id
        const optionId = o.value;
        const locationEntity = locationsById[optionId];
        const children = refinedLocationChildrenById[optionId];
        const { isSelectSubnationalsDisabled } = refinementFiltersConfig[LOCATION_ID_KEY];

        if (!locationEntity) {
            return null;
        }

        // Display simple list item
        if (
            isSelectSubnationalsDisabled ||
            isLocationTypeGlobal(locationEntity.location_type_id) ||
            children == null
        ) {
            return <ListItem disabled={optionDisabledGetter(o)}>{optionLabelGetter(o)}</ListItem>;
        }

        const selectedFilterIds = value || [];
        const directSubnationalsIds: any[] = children.map((i) => i);

        const areAllDirectChildrenSelected = directSubnationalsIds.every((i) =>
            selectedFilterIds.includes(i)
        );

        const handleSubnationalsClick = (ev: React.MouseEvent) => {
            ev.stopPropagation();

            // calculate new ids
            const newValue = areAllDirectChildrenSelected
                ? difference(selectedFilterIds, directSubnationalsIds)
                : union(selectedFilterIds, directSubnationalsIds);

            handleChange(newValue);
        };

        const subnationalIconSrc = areAllDirectChildrenSelected
            ? '/icons/ic-multiselect-all.svg'
            : '/icons/ic-multiselect-some.svg';

        const isSubnationalsButtonDisabled = optionDisabledGetter(o);

        return (
            <FlexRow justify="space-between">
                <div>{optionLabelGetter(o)}</div>
                <SubnationalButton
                    align="center"
                    itemsSpacing={8}
                    disabled={isSubnationalsButtonDisabled}
                    onClick={!isSubnationalsButtonDisabled ? handleSubnationalsClick : undefined}
                >
                    <SubnationalSvgIcon
                        data-testid="select-subnationals-icon"
                        src={subnationalIconSrc}
                        size="lg"
                        disabled={isSubnationalsButtonDisabled}
                        isActive={areAllDirectChildrenSelected}
                    />
                </SubnationalButton>
            </FlexRow>
        );
    };

    let control;
    switch (granularityKey) {
        case MEASURE_ID_KEY:
            control = (
                <ChartDropdown
                    label={label}
                    id={granularityKey}
                    filterKey={granularityKey}
                    filterValue={selectedValue}
                    defaultValue={defaultFilters[granularityKey]}
                    {...refinementFiltersConfig[granularityKey]}
                />
            );
            break;

        case YEAR_KEY:
        case DAY_KEY:
        case MONTH_COUNT_KEY: {
            const filterGranularity = fallbackGranularity[granularityKey];

            const [startValue, endValue] = (!isEmpty(filterGranularity) &&
                firstAndLastFromArray(filterGranularity)) || [0, 0];

            const showNowcastsToggle =
                granularityKey === YEAR_KEY &&
                organization?.forecast_data_calculation_is_allowed &&
                forecastDataCalculationMap;

            const nowcastsToggle = showNowcastsToggle && (
                <NowcastsToggleContainer>
                    <Toggle
                        key={String(includeNowcasts && hasNowcastYearsBeyondPublished)}
                        checked={includeNowcasts}
                        disabled={!hasNowcastYearsBeyondPublished}
                        name="include-nowcasts"
                        label={<BodyText3>{_('refinement_filter_include_nowcasts')}</BodyText3>}
                        onChange={handleToggleIncludeNowcasts}
                    />
                </NowcastsToggleContainer>
            );

            switch (mode || refinementFiltersConfig[granularityKey].mode) {
                case 'slider':
                    control = (
                        <>
                            <ChartRangeSlider
                                key={
                                    selectedValue +
                                    startValue.toString() +
                                    endValue.toString() +
                                    mode +
                                    includeNowcasts
                                }
                                id={granularityKey}
                                label={label}
                                filterKey={granularityKey}
                                filterValue={selectedValue}
                                onChangeComplete={onChange}
                                shouldIncludeNowcasts={
                                    granularityKey === YEAR_KEY ? includeNowcasts : false
                                }
                                {...refinementFiltersConfig[granularityKey]}
                            />
                            {nowcastsToggle}
                        </>
                    );
                    break;

                case 'dropdown':
                    control = (
                        <>
                            <ChartDropdown
                                isMulti
                                disableStatusCheck
                                placeholder={_.get(`refinement_filter_${granularityKey}_undefined`)}
                                id={granularityKey}
                                label={label}
                                filterKey={granularityKey}
                                filterValue={selectedValue}
                                defaultValue={defaultFilters[granularityKey]}
                                onChange={onChange}
                                shouldIncludeNowcasts={
                                    granularityKey === YEAR_KEY ? includeNowcasts : false
                                }
                                {...refinementFiltersConfig[granularityKey]}
                            />
                            {nowcastsToggle}
                        </>
                    );
                    break;
            }
            break;
        }

        case COMORBID_CAUSE_ID_KEY:
            control = (
                <ChartHierarchicalDropdown
                    label={label}
                    id={granularityKey}
                    filterKey={granularityKey}
                    filterValue={selectedValue}
                    defaultValue={defaultFilters[granularityKey]}
                    onChange={onChange}
                    placeholder={_.get('search_cause_id')}
                    useLetterPrefix={false}
                    loadHierarchy={() => Promise.resolve(entityHierarchies.causes)}
                    optionLabelGetter={(o) => o.label}
                    optionValueGetter={(o) => o.value}
                    preprocessOptions={(options) => {
                        const areAllItemsAtSameLevel = uniqBy('level', options).length === 1;
                        const availableOptions = options.filter((o) =>
                            fallbackGranularity[granularityKey].includes(o.value)
                        );

                        return areAllItemsAtSameLevel
                            ? sortBy('label', availableOptions)
                            : availableOptions;
                    }}
                    {...refinementFiltersConfig[granularityKey]}
                />
            );
            break;

        case CAUSE_ID_KEY:
            control = (
                <ChartHierarchicalDropdown
                    label={label}
                    id={granularityKey}
                    filterKey={granularityKey}
                    filterValue={selectedValue}
                    defaultValue={defaultFilters[granularityKey]}
                    onChange={onChange}
                    placeholder={_.get('search_cause_id')}
                    useLetterPrefix={false}
                    loadHierarchy={() => Promise.resolve(entityHierarchies.causes)}
                    optionLabelGetter={(o) => o.label}
                    optionValueGetter={(o) => o.value}
                    {...refinementFiltersConfig[granularityKey]}
                />
            );
            break;

        case LOCATION_ID_KEY: {
            if (!(entityHierarchies.locations && locationsById && refinedLocationChildrenById)) {
                return null;
            }

            const locationGranularity = granularity[granularityKey];
            const locationFallbackGranularity = fallbackGranularity[granularityKey];

            control = (
                <ChartHierarchicalDropdown
                    key={(locationGranularity || []).map(({ id }) => id).join('_')}
                    label={label}
                    id={granularityKey}
                    filterKey={granularityKey}
                    filterValue={selectedValue}
                    defaultValue={defaultFilters[granularityKey]}
                    onChange={onChange}
                    placeholder={_.get('search_location_id')}
                    useLetterPrefix={false}
                    limitHierarchyDepth={8}
                    loadHierarchy={() => Promise.resolve(entityHierarchies.locations)}
                    optionLabelGetter={(o) => o.label}
                    optionValueGetter={(o) => o.value}
                    optionDisabledGetter={(o) => {
                        // find entity of option
                        const optionId = o.value;
                        const locationEntity = locationsById[optionId];
                        // when not in the refined granularity should be disabled
                        let disabled = !locationFallbackGranularity.includes(optionId);
                        // when option is a subnational of a country which is disabled
                        // it should also be disabled
                        if (
                            !disabled &&
                            isLocationTypeSubnational(locationEntity.location_type_id)
                        ) {
                            let level = locationEntity.level;
                            // traverse location parents checking if they are
                            // disabled and available
                            while (!disabled && level > 0) {
                                level--;
                                const parentId = locationEntity[`level${level}_parent_id`];
                                if (locationsById[parentId]) {
                                    const parentIdLocationType =
                                        locationsById[parentId].location_type_id;

                                    if (isLocationTypeCountryOrSubnational(parentIdLocationType)) {
                                        const isParentAvailable =
                                            locationGranularity.includes(parentId);
                                        const isParentDisabled =
                                            !locationFallbackGranularity.includes(parentId);

                                        disabled = isParentAvailable && isParentDisabled;
                                    }
                                }
                            }
                        }
                        return disabled;
                    }}
                    optionSearchTermGetter={(o) => {
                        // find entity of option
                        const optionId = o.value;
                        const locationEntity = locationsById[optionId];

                        if (!locationEntity) {
                            return null;
                        }

                        let level = locationEntity.level;
                        let searchTerm = _(`location_${optionId}`);
                        // when using search functionality subnationals below country
                        // should have all parents visible up to a country in search results
                        // the rest should have only direct parent
                        while (
                            level > 0 &&
                            isLocationTypeCountryOrSubnational(locationEntity.location_type_id)
                        ) {
                            level--;
                            const parentId = locationEntity[`level${level}_parent_id`];
                            if (locationsById[parentId]) {
                                const parentIdLocationType =
                                    locationsById[parentId].location_type_id;

                                if (isLocationTypeCountryOrSubnational(parentIdLocationType)) {
                                    const parentSearchTerm = _(`location_${parentId}`);
                                    searchTerm = `${searchTerm} ${parentSearchTerm}`;
                                }
                            }
                        }
                        return searchTerm;
                    }}
                    renderOption={handleRenderOption}
                    {...refinementFiltersConfig[granularityKey]}
                />
            );
            break;
        }

        case RACE_ID_KEY:
            control = (
                <RaceDropdown
                    label={label}
                    id={granularityKey}
                    filterValue={selectedValue}
                    defaultValue={defaultFilters[granularityKey]}
                    onChange={onChange}
                    {...refinementFiltersConfig[granularityKey]}
                />
            );
            break;

        case AGE_GROUP_ID_KEY:
            switch (mode || refinementFiltersConfig[granularityKey].mode) {
                case 'slider':
                    control = (
                        <ChartRangeSlider
                            key={'' + selectedValue + mode}
                            id={granularityKey}
                            label={label}
                            filterKey={granularityKey}
                            filterValue={selectedValue}
                            onChangeComplete={onChange}
                            {...refinementFiltersConfig[granularityKey]}
                        />
                    );
                    break;

                default:
                    control = (
                        <AgeGroupsDropdown
                            label={label}
                            id={granularityKey}
                            filterKey={granularityKey}
                            filterValue={selectedValue}
                            defaultValue={defaultFilters[granularityKey]}
                            onChange={onChange}
                            {...refinementFiltersConfig[granularityKey]}
                        />
                    );
                    break;
            }

            break;

        case RESPONSE_ID_KEY:
            control = (
                <ChartDropdown
                    label={label}
                    id={granularityKey}
                    filterKey={granularityKey}
                    filterValue={selectedValue}
                    defaultValue={defaultFilters[granularityKey]}
                    preprocessOptions={preprocessOptions}
                    onChange={onChange}
                    {...refinementFiltersConfig[granularityKey]}
                />
            );
            break;

        case FORECAST_SCENARIO_ID_KEY: {
            let localePrefix = getLocalePrefixForGranularityKey(granularityKey);
            if (dataTool === HEALTH_THREATS_METRICS_PATH) {
                localePrefix = `ght_records_${localePrefix}`;
            }
            control = (
                <ChartDropdown
                    label={label}
                    id={granularityKey}
                    filterKey={granularityKey}
                    filterValue={selectedValue}
                    defaultValue={defaultFilters[granularityKey]}
                    preprocessOptions={preprocessOptions}
                    onChange={onChange}
                    optionLabelGetter={(o) => _.withPrefix(localePrefix, o)}
                    {...refinementFiltersConfig[granularityKey]}
                />
            );
            break;
        }

        case VACCINATION_STATUS_ID_KEY:
            control = (
                <VaccinationStatusDropdown
                    label={label}
                    id={granularityKey}
                    filterValue={selectedValue}
                    defaultValue={defaultFilters[granularityKey]}
                    onChange={onChange}
                    {...refinementFiltersConfig[granularityKey]}
                />
            );
            break;

        default:
            control = (
                <ChartDropdown
                    label={label}
                    id={granularityKey}
                    filterKey={granularityKey}
                    filterValue={selectedValue}
                    defaultValue={defaultFilters[granularityKey]}
                    onChange={onChange}
                    preprocessOptions={preprocessOptions}
                    {...refinementFiltersConfig[granularityKey]}
                />
            );
            break;
    }

    const isSelectAllEnabled = !SELECT_ALL_DISABLED_KEYS.includes(granularityKey);

    return (
        <FlexRow
            key={granularityKey}
            itemsSpacing={isCompact ? 10 : 30}
            align={isCompact ? 'flex-start' : 'center'}
            justify="space-between"
            data-testid={`refinement-filter-${granularityKey}`}
        >
            <FlexRow align="center" grow={1}>
                <div style={{ width: '100%' }}>{control}</div>
            </FlexRow>
            {(!hideSelectAll || !hideDelete) && (
                <FlexRow
                    style={{
                        minWidth: isCompact
                            ? COMPACT_BUTTON_SIZE
                            : areRefinementFiltersSmartOptionsDisabled
                              ? 80
                              : 200,
                    }}
                >
                    {!hideSelectAll && isSelectAllEnabled && (
                        <SmartFilterOptionSelector
                            filterKey={granularityKey}
                            selectedConditions={selectedConditions}
                            onChange={onChange}
                            hideDelete={hideDelete}
                            onlySelectAllKeys={onlySelectAllKeys}
                        />
                    )}
                    {!hideDelete && (
                        <FlexRow grow={1} justify="flex-end" align="center">
                            <RemoveButton
                                onClick={() => handleRemoveRefinementClick(granularityKey)}
                            >
                                {_('data_explorer_remove')}
                            </RemoveButton>
                        </FlexRow>
                    )}
                </FlexRow>
            )}
        </FlexRow>
    );
};

export default RefinementFilter;
